import React, { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import { Sidebar } from "@/components/Sidebar";
import { TopBar } from "@/components/TopBar";

export function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showUp, setShowUp] = useState(false);

  const mainRef = useRef<HTMLElement | null>(null);

  // ✅ LTR / RTL (source of truth)
  const dir =
    (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const isRTL = dir === "rtl";

  useEffect(() => {
    const el = mainRef.current;

    const update = () => {
      const mainScrollable = !!el && el.scrollHeight > el.clientHeight + 2;

      if (mainScrollable && el) {
        setShowUp(el.scrollTop > 250);
      } else {
        setShowUp(window.scrollY > 250);
      }
    };

    update();

    el?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);

    return () => {
      el?.removeEventListener("scroll", update);
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const scrollToTop = () => {
    const el = mainRef.current;
    const mainScrollable = !!el && el.scrollHeight > el.clientHeight + 2;

    if (mainScrollable && el) {
      el.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const [searchOpen, setSearchOpen] = useState(false);
  return (
    <div className="min-h-screen flex w-full" dir={dir}>
      {/* Mobile overlay */}
      {mobileOpen && (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu overlay"
        />
      )}


      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed((s) => !s)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* ✅ MAIN CONTENT AREA (NO INLINE STYLES) */}
      <div
        className={[
          "flex-1 flex flex-col min-h-0 transition-[margin] duration-300",
          // only shift content on desktop
          !isRTL
            ? collapsed
              ? "lg:ml-16"
              : "lg:ml-64"
            : collapsed
              ? "lg:mr-16"
              : "lg:mr-64",
          // always no margin on mobile
          "ml-0 mr-0",
        ].join(" ")}
      >
        <TopBar
          onMenuClick={() => setMobileOpen(true)}
          onSearchClick={() => setSearchOpen(true)}
        />



        <main
          ref={mainRef as any}
          className="flex-1 min-h-0 min-w-0 p-3 sm:p-6 overflow-y-auto overflow-x-hidden"
        >
          
          <Outlet />
        </main>

       <footer className="py-4 px-6 text-center text-sm text-muted-foreground border-t border-border">
  © {new Date().getFullYear()} Goldstep Capital. All rights reserved.
</footer>

        {/* Floating Scroll Up Button */}
        <button
          type="button"
          onClick={scrollToTop}
          aria-label="Scroll to top"
          className={[
            "fixed bottom-6 z-50",
            isRTL ? "left-6" : "right-6",
            "h-14 w-14 rounded-full",
            "bg-[#7C3AED] hover:bg-[#6D28D9]",
            "shadow-xl shadow-purple-500/30",
            "flex items-center justify-center",
            "transition-all duration-300",
            showUp
              ? "opacity-100 translate-y-0 pointer-events-auto"
              : "opacity-0 translate-y-3 pointer-events-none",
          ].join(" ")}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            className="text-white"
            aria-hidden="true"
          >
            <path
              d="M12 19V6"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <path
              d="M6.5 11.5L12 6l5.5 5.5"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}